/**
 * Base IndexedDB et magasins — EF-FIC-001.
 *
 * Le document n'est **jamais** réécrit en bloc : une exclusion réécrit un seul
 * enregistrement de `pois`, et la trace de calcul, de loin la plus volumineuse,
 * est écrite à l'importation du parcours et plus jamais ensuite. Ce découpage
 * conditionne ENF-014.
 */

import { DomainError } from '../core/errors';

export const DATABASE_NAME = 'roadbookultra';

/** Le numéro n'est incrémenté que lorsque l'organisation des magasins change. */
export const DATABASE_VERSION = 1;

export const STORES = {
  meta: 'meta',
  route: 'route',
  pois: 'pois',
  results: 'results',
  settings: 'settings',
  searchState: 'searchState',
  purgeBin: 'purgeBin',
  lock: 'lock',
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

/** Clé des magasins à enregistrement unique. */
export const SINGLETON_KEY = 'current';

const SINGLETON_STORES: readonly StoreName[] = [
  STORES.meta,
  STORES.route,
  STORES.results,
  STORES.searchState,
  STORES.lock,
];

const KEYED_STORES: readonly StoreName[] = [STORES.pois, STORES.purgeBin];

function storageError(message: string, cause: unknown = null): DomainError {
  const detail = cause instanceof Error ? cause.message : cause === null ? null : String(cause);
  if (detail !== null && /quota|QuotaExceeded/i.test(detail)) {
    return new DomainError('storage/quota', `${message} Le quota de stockage local est atteint.`, detail);
  }
  return new DomainError('storage/write-failed', message, detail);
}

export function toPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = (): void => {
      resolve(request.result);
    };
    request.onerror = (): void => {
      reject(storageError("L'accès au stockage local a échoué.", request.error));
    };
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = (): void => {
      resolve();
    };
    transaction.onerror = (): void => {
      reject(storageError("L'enregistrement local a échoué.", transaction.error));
    };
    transaction.onabort = (): void => {
      reject(storageError("L'enregistrement local a été interrompu.", transaction.error));
    };
  });
}

/**
 * Ouvre la base et exécute la migration éventuelle. Une migration qui échoue
 * interrompt sa transaction, laissant les données de la version précédente
 * intactes et exploitables — c'est ce qui rend récupérable CA-035.
 */
export function openDatabase(
  name: string = DATABASE_NAME,
  version: number = DATABASE_VERSION,
): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(
      new DomainError(
        'storage/unavailable',
        "IndexedDB est indisponible : aucune modification métier ne peut être enregistrée.",
      ),
    );
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, version);

    request.onupgradeneeded = (): void => {
      const database = request.result;
      for (const store of SINGLETON_STORES) {
        if (!database.objectStoreNames.contains(store)) database.createObjectStore(store);
      }
      for (const store of KEYED_STORES) {
        if (!database.objectStoreNames.contains(store)) database.createObjectStore(store, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(STORES.settings)) {
        database.createObjectStore(STORES.settings);
      }
    };

    request.onsuccess = (): void => {
      resolve(request.result);
    };
    request.onerror = (): void => {
      reject(storageError("L'ouverture du stockage local a échoué.", request.error));
    };
    request.onblocked = (): void => {
      reject(
        new DomainError(
          'storage/unavailable',
          'Le stockage local est bloqué par une autre fenêtre de l’application.',
        ),
      );
    };
  });
}

/**
 * Exécute une transaction unique couvrant les magasins concernés. Un échec
 * laisse intacte la dernière génération valide, sans état intermédiaire.
 */
export async function runTransaction(
  database: IDBDatabase,
  stores: readonly StoreName[],
  mode: IDBTransactionMode,
  work: (transaction: IDBTransaction) => void,
): Promise<void> {
  const transaction = database.transaction([...stores], mode);
  const done = transactionDone(transaction);
  try {
    work(transaction);
  } catch (error) {
    transaction.abort();
    throw error;
  }
  await done;
}

export async function readValue<T>(
  database: IDBDatabase,
  store: StoreName,
  key: IDBValidKey,
): Promise<T | null> {
  const transaction = database.transaction([store], 'readonly');
  const result = await toPromise<T | undefined>(transaction.objectStore(store).get(key) as IDBRequest<T | undefined>);
  return result ?? null;
}

export async function readAll<T>(database: IDBDatabase, store: StoreName): Promise<readonly T[]> {
  const transaction = database.transaction([store], 'readonly');
  const result = await toPromise<T[]>(transaction.objectStore(store).getAll() as IDBRequest<T[]>);
  return result;
}

export async function writeValue(
  database: IDBDatabase,
  store: StoreName,
  value: unknown,
  key?: IDBValidKey,
): Promise<void> {
  await runTransaction(database, [store], 'readwrite', (transaction) => {
    const objectStore = transaction.objectStore(store);
    if (key === undefined) objectStore.put(value);
    else objectStore.put(value, key);
  });
}

export async function clearStores(database: IDBDatabase, stores: readonly StoreName[]): Promise<void> {
  await runTransaction(database, stores, 'readwrite', (transaction) => {
    for (const store of stores) transaction.objectStore(store).clear();
  });
}

/** Espace utilisé, lorsque la Storage API le rapporte (ENF-020). */
export async function estimateUsage(): Promise<{ usageBytes: number | null; quotaBytes: number | null }> {
  const storage = globalThis.navigator?.storage;
  if (storage === undefined || typeof storage.estimate !== 'function') {
    return { usageBytes: null, quotaBytes: null };
  }
  try {
    const estimate = await storage.estimate();
    return { usageBytes: estimate.usage ?? null, quotaBytes: estimate.quota ?? null };
  } catch {
    return { usageBytes: null, quotaBytes: null };
  }
}
